<template>
    <div class="head">
        <div class="title">{{ viewTitle }}</div>
        <GoHomeButton @click.prevent="onGoHomeButtonClick" />
        <UserMenuComponent />
    </div>

    <div class="itemlist scrollable">
        <BillSummarySection
            :isUserWaiterOrAdmin="isUserWaiterOrAdmin"
            @onClickOrder="
                async (orderId: string) => {
                    await showOrderReceipt(orderId);
                }
            "
        />
    </div>

    <div class="bottom">
        <div class="selected">{{ selectedArticlesText }}</div>
        <PayButtonComponent
            :action="trans.general.continue"
            :disabled="!isPayButtonEnabled"
            :disabledMessage="payButtonDisabledMessage"
            :amount="totalAmount"
            @click="onProceedButtonClick"
        />
    </div>
</template>

<script setup lang="ts">
    import { appEvents } from "@/app/App";
    import { TastierClient } from "@/app/TastierClient";
    import { ViewTransition } from "@/app/controller/BaseController";
    import { BillController_new } from "@/app/controller/BillController_new";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import { OrderSummary } from "@/app/ui/OrderSummary";
    import { Popup } from "@/app/ui/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { IBill, IBillItem, ISelectedItem, IUserItemSelection } from "@/data/model/Bill";
    import type { ICustomerOrder } from "@/data/model/Customer";
    import { UserItemSelectionChangeReason } from "@/data/model/Enums";
    import type { BillItemSelectionChangedEvent } from "@/data/model/Events";
    import { useBillStore } from "@/data/stores/bill";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { translations } from "@/shared/localization/translations";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, onBeforeUnmount, onMounted, ref } from "vue";

    import BillSummarySection from "@/app/components/bill/BillSummarySection.vue";
    import PayButtonComponent from "@/app/components/footer/PayButtonComponent.vue";
    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";

    const bill = useBillStore();

    // header
    const isViewVisible = ref(false);

    BillController_new.onShown.add(onBecomeVisible);
    BillController_new.onHidden.add(onBecomeInvisible);

    // init components
    onMounted(() => {
        constructor();
    });

    onBeforeUnmount(() => {});

    const viewTitle = ref<string>(trans.bill.title);
    const selectedArticlesText = computed(() =>
        bill.isFullyPaid ? trans.bill.fullyPaid : `${bill.totalItemsSelected} ${trans.bill.selectedItems}`
    );

    const totalAmount = computed(() => toMoneyWithSign(bill.totalPayableAfterDiscount, true));
    const isPayButtonEnabled = computed(() => bill != null && hasSelectedUnpaidItems.value);
    const hasSelectedUnpaidItems = computed(() => !bill.isFullyPaid && bill.totalItemsSelected > 0);
    const payButtonDisabledMessage = computed(() => {
        if (bill.currentBill === undefined) {
            return trans.bill.waitingForOrder;
        }
        if (!hasSelectedUnpaidItems.value) {
            return toMoneyWithSign(0, true);
        }

        return undefined;
    });

    const isUserWaiterOrAdmin = ref<boolean>(false);
    const itemListElement = ref<HTMLDivElement | null>(null);

    function constructor() {
        isUserWaiterOrAdmin.value = TastierClient.identityContext.isWaiterOrAdmin();

        if (itemListElement.value) {
            orderSummary = new OrderSummary().appendTo($(itemListElement.value!));
        }
        onBecomeVisible();
    }

    async function onBecomeVisible() {
        isViewVisible.value = true;

        const serviceDesignation = Helper.getCurrentServiceDesignation();
        viewTitle.value = serviceDesignation === undefined ? trans.bill.title : serviceDesignation;
        redrawBill(bill.currentBill ?? undefined);

        dataEvents.appSocket.ItemSelectionChanged.add(handleItemSelectionChanged);
        TastierClient.context.onItemSelectionChange.add(onItemSelectionChanged);
        TastierClient.context.onBillChange.add(redrawBill);
    }

    function onBecomeInvisible() {
        isViewVisible.value = false;

        dataEvents.appSocket.ItemSelectionChanged.remove(handleItemSelectionChanged);
        TastierClient.context.onItemSelectionChange.remove(onItemSelectionChanged);
        TastierClient.context.onBillChange.remove(redrawBill);
    }

    let orderSummary: OrderSummary | undefined;

    function onItemSelectionChanged(a: IUserItemSelection[]) {
        orderSummary?.updateSelection(a);
    }

    async function showOrderReceipt(orderId) {
        const order = await Factory.getInstance<CustomerDao>(CustomerDao).getOrder(orderId);
        showReceipt(order);
    }

    function showReceipt(data: ICustomerOrder) {
        const showReceiptData = {
            order: data,
            transition: ViewTransition.SlideLeft,
        };
        appEvents.OnShowReceipt.dispatch(showReceiptData);
    }

    function redrawBill(bill?: IBill) {
        orderSummary?.reset();
        orderSummary?.updateBill(bill);
    }

    function handleItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (eventData.identityId !== TastierClient.identityContext.getIdentityId()) {
            return;
        }

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            showSelectionChangedPopup();
        }
    }

    function onGoHomeButtonClick() {
        BillController_new.onShowServices.dispatch();
    }

    function onProceedButtonClick() {
        if (!isPayButtonEnabled.value) {
            return;
        }

        if (
            !showNotAllItemsSelectedWarningPopupIfNeeded(() => {
                BillController_new.onContinue.dispatch();
            })
        ) {
            BillController_new.onContinue.dispatch();
        }
    }

    function getNotAllSelectedUnpaidItems(selectedItems: ISelectedItem[]): Array<IBillItem> {
        const notAllSelectedItems = new Array<IBillItem>();

        const billItems = bill.currentBill?.unpaidItems ?? [];
        for (const selectedItem of selectedItems) {
            const billItem = billItems.find((item) => item.id === selectedItem.billItemId);
            if (billItem == null) {
                continue;
            }

            if (billItem.quantity > selectedItem.quantity) {
                notAllSelectedItems.push(billItem);
            }
        }

        return notAllSelectedItems;
    }

    // Returns true if the popup needs to be shown, otherwise returns false
    function showNotAllItemsSelectedWarningPopupIfNeeded(onClickContinue: () => void): boolean {
        // If user is waiter or admin, we should not show the popup
        if (isUserWaiterOrAdmin.value) {
            return false;
        }

        const selectedItemsList = bill.selectedItemsList;
        if (selectedItemsList == null || selectedItemsList.length === 0) {
            return false;
        }

        const notAllSelectedBillItems = getNotAllSelectedUnpaidItems(selectedItemsList);
        if (notAllSelectedBillItems.length > 0) {
            const billItemNames = notAllSelectedBillItems.map((item) => getNameInLocationLanguage(item, false)).join(", ");
            Popup.show(
                translations.pay.notAllItemsSelected.replace("{0}", billItemNames),
                () => {
                    onClickContinue();
                },
                translations.general.yesContinue,
                translations.general.notSure,
                "notAllItemsSelectedPopup",
                undefined,
                true,
                true
            );

            return true;
        }

        return false;
    }

    function showSelectionChangedPopup() {
        Popup.show(
            translations.pay.failedBillInvalidSelection,
            undefined,
            translations.general.ok,
            undefined,
            "pmtconfirm"
        );
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .bill {
        display: flex;
        flex-direction: column;

        > .head {
            flex: 0 0 auto;
        }

        > .bill-items-section {
            flex: 1 1 auto;
            position: relative;
        }

        > .itemlist {
            flex: 1 1 auto;
            position: relative;
        }

        .ordersummary {
            position: absolute;
            top: 0;
            bottom: 104px;
            left: 0;
            right: 0;
        }

        > .bottom {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: #fff;
            padding: 9px 12px 10px 12px;
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            z-index: 300;
            pointer-events: none;

            > .selected {
                font-size: 14px;
                text-align: center;
                line-height: 20px;
                letter-spacing: 0.75px;
                padding-bottom: 9px;
            }
        }

        .order-item-quantity {
            &.indent {
                &.qty-1 {
                    flex-basis: 1.25em;
                }

                &.qty-2 {
                    flex-basis: 1.85em;
                }

                &.qty-3 {
                    flex-basis: 2.45em;
                }

                &.qty-4 {
                    flex-basis: 3.05em;
                }
            }
        }

        .bill-items-section h3 {
            font-size: 12px;
            border-bottom: none;
        }

        .paid-line-item {
            .item-price {
                opacity: 0.7;
            }

            .order-item-quantity {
                &.indent {
                    padding-left: 0;
                }
            }
        }
    }
</style>
